/**
 * Browser-only JWT helpers.
 *
 * Decode the payload of a JWT WITHOUT verifying the signature. Used purely as
 * a client-side hint (e.g. "is the access token already expired?") to avoid
 * triggering /auth/refresh on 401s that are not actually about token expiry.
 *
 * Never use these for any security decision — only the API can validate the
 * token. Treat unknown / malformed tokens as "expired" so callers fall back
 * to the existing refresh path.
 */

export function decodeJwtPayload(token) {
    if (typeof token !== 'string' || token.length === 0) return null;

    const parts = token.split('.');
    if (parts.length < 2) return null;

    try {
        let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';

        const binary = atob(b64);
        const json = decodeURIComponent(
            binary
                .split('')
                .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
                .join('')
        );

        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function isJwtExpired(token, { skewSeconds = 5 } = {}) {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return true;

    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= payload.exp - skewSeconds;
}
